import { Address, Contragent, IListData, Locality } from '@models';
import { roundToPrecision } from '@shared';
import * as moment from 'moment';
import { InvoiceContact } from '../../models/invoice/invoice-contact.model';
import { InvoicePaymentMethod } from '../../models/invoice/invoice-payment-method.model';
import { InvoiceState } from '../../models/invoice/invoice-state.model';
import { getOutgoingDocumentsByPhoneResponse, Invoice } from '../../models/invoice/invoice.model';
import { ServiceType, ServiceTypeParts } from './invoice-api.enums';
import { states } from './invoice-states.config';
import { getCounterPartyType } from './invoice.api';

export function splitName(name: string) {
  if (!name) {
    return ['', '', ''];
  }
  return [name, '', ''];
}

export function coerceToNumber(el: any): number {
  return !isNaN(parseFloat(el)) && !!parseFloat(el) ? parseFloat(el) : 0;
}

export function setAddressType(addressDescription: string) {
  return addressDescription.toLowerCase().indexOf('відділення') > -1
    ? 'Warehouse'
    : addressDescription.toLowerCase().indexOf('поштомат') > -1
      ? 'Postbox'
      : 'Address';
}

export function getState(statusCode) {
  return states[statusCode];
}

export function isInternational(rawInvoice: any) {
  return (
    rawInvoice.InternationalDeliveryType === 'Import' ||
    rawInvoice.InternationalDeliveryType === 'Export'
  );
}

export function checkAllowManageButton(rawInvoice: any): boolean {
  return rawInvoice.possibilityCreateRedirecting ||
    rawInvoice.possibilityCreateReturn ||
    rawInvoice.possibilityChangeEW ||
    rawInvoice.possibilityTermExtension;
}

export function getServiceTypeParts(serviceType: string): string[] | null {
  if (!serviceType) {
    return null;
  }

  const split = serviceType.split(/(?=[A-Z])/);

  if (split.length === 2) {
    return split;
  }

  return null;
}

export function getSenderServiceType(serviceType: string): ServiceTypeParts | null {
  const parts = getServiceTypeParts(serviceType);

  if (!parts) {
    return null;
  }

  return Object.values(ServiceTypeParts).includes(parts[0] as ServiceTypeParts)
    ? parts[0] as ServiceTypeParts
    : null;
}

export function getRecipientServiceType(serviceType: string): ServiceTypeParts | null {
  if (!serviceType) {
    return null;
  }

  const parts = getServiceTypeParts(serviceType);

  if (!parts) {
    return null;
  }

  return Object.values(ServiceTypeParts).includes(parts[1] as ServiceTypeParts)
    ? parts[1] as ServiceTypeParts
    : null;
}

export function createShortDescription(description: string, crop: boolean = true): string {
  if (!description) {
    return '';
  }

  // Special case
  const textToKeepUncutDescription = 'Пункт приймання-видачі';

  if (crop && !description.includes(textToKeepUncutDescription)) {
    return description.slice(0, description.indexOf(':'));
  } else {
    return description;
  }
}

export function getRedirectingReturnIndication(invoice: Invoice): void {
  if (!invoice.OrderingAdditionalServices?.length) {
    return;
  }

  const latestService = invoice.OrderingAdditionalServices
    .filter(
      (a) =>
        a.OrderType === 'Redirecting'
        || a.OrderType === 'CargoReturn',
    )
    .filter((a) =>
      a.Status === 'CreateEWCargoReturn'
      || a.Status === 'StoreInProcessing'
      || a.Status === 'Received',
    )
    .sort(
      (a, b) =>
        moment(b.DateTime).valueOf() - moment(a.DateTime).valueOf(),
    )[0];

  if (!latestService) {
    return;
  }

  invoice.RedirectingReturnIndication = { icon: '', tooltipText: '' };

  if (latestService.OrderType === 'Redirecting') {
    invoice.RedirectingReturnIndication.icon = 'call_split';
    if (latestService.Status === 'Received' || latestService.Status === 'StoreInProcessing') {
      invoice.RedirectingReturnIndication.tooltipText = `Оформлено заявку на переадресацію ${latestService.Number ?? ''}`;
    }

    if (latestService.Status === 'CreateEWCargoReturn') {
      invoice.RedirectingReturnIndication.tooltipText = `Відправлення переадресовано по ЕН ${latestService.EWRedirectingNumber ?? ''}`;
    }
  }

  if (latestService.OrderType === 'CargoReturn') {
    invoice.RedirectingReturnIndication.icon = 'import_export';
    if (latestService.Status === 'Received' || latestService.Status === 'StoreInProcessing') {
      invoice.RedirectingReturnIndication.tooltipText = `Оформлено заявку на повернення ${latestService.Number ?? ''}`;
    }

    if (latestService.Status === 'CreateEWCargoReturn') {
      invoice.RedirectingReturnIndication.tooltipText = `Відправлення повернено по ЕН ${latestService.EWCargoReturnNumber ?? ''}`;
    }
  }
}

export function _bindListToModelType2(
  data: getOutgoingDocumentsByPhoneResponse,
): IListData<Invoice> {
  return {
    total: data.info.totalCount,
    list:
      data.data[0]?.result?.map((el: any) => {
        if (el.OrderingAdditionalServices) {
          getRedirectingReturnIndication(el);
        }
        el.docNumber = el.Number;
        el.createTime = moment(el.CreateTime).toDate();
        el.dateTime = moment(el.DateTime).toDate();
        el.dateFirstDayStorage =
          el?.FirstDayStorage !== '0001-01-01 00:00:00'
            ? el.FirstDayStorage
            : null;
        let userNameParts: string[] = splitName(el.SenderName);
        el.sender = new InvoiceContact(
          userNameParts[1],
          userNameParts[0],
          null,
        );
        el.sender.phone = el.PhoneSender;
        el.note = el.Note;
        el.weight =
          Number(el.CalculatedWeight) > 0
            ? el.CalculatedWeight
            : el.DocumentWeight;
        if (el.CounterpartySender) {
          el.sender.contragent = new Contragent(el.CounterpartySender);
        }
        el.counterpartySenderDescription = el.CounterpartySenderDescription;
        el.counterpartySender = el.CounterpartySenderDescription;
        el.SenderAddressShortDescription = createShortDescription(
          el.SenderAddressDescription,
          getSenderServiceType(el.ServiceType) === ServiceTypeParts.WAREHOUSE,
        );
        el.cost = el.Cost;

        el.moneyTransferAmount =
          el.MoneyTransferCash2Card === '0'
            ? coerceToNumber(el.MoneyTransferAmount)
            : null;
        el.moneyTransferCash2Card =
          el.MoneyTransferCash2Card === '1'
            ? coerceToNumber(el.MoneyTransferAmount)
            : null;

        userNameParts = splitName(el.RecipientName);
        el.recipient = new InvoiceContact(
          userNameParts[1],
          userNameParts[0],
          null,
        );
        el.recipientContactPhone = el.RecipientContactPhone || '';
        el.recipient.phone = el.PhoneRecipient;
        el.recipient.city = new Locality(el.CityRecipient);
        el.recipient.city.name = el.CityRecipientDescription;

        if (el.MoneyTransferAmount) {
          el.Cash2CardPAN = el.CardMaskedNumber;
          el.backDeliveryPayer = el.MoneyTransferPayerCommission;
          el.backMoney = coerceToNumber(el.MoneyTransferAmount);
        }

        el.recipientCounterpartyType =
          el.RecipientCounterpartyType ||
          getCounterPartyType(el.CounterpartyRecipientDescription) ||
          '';

        if (el.CounterpartyRecipient) {
          el.recipient.contragent = new Contragent(el.CounterpartyRecipient);
        }

        el.recipientAddress = new Address();
        if (el.RecipientAddressDescription) {
          el.recipientAddress.description = el.RecipientAddressDescription;
          el.recipientAddress.shortDescription = createShortDescription(
            el.RecipientAddressDescription,
            getRecipientServiceType(el.ServiceType) === ServiceTypeParts.WAREHOUSE,
          );
          el.recipientAddress.type = setAddressType(
            el.RecipientAddressDescription,
          );
        }

        el.senderAddress = new Address();
        el.senderAddress.city = new Locality(el.CitySender);

        if (el.SenderAddressDescription) {
          el.senderAddress.shortDescription = createShortDescription(
            el.SenderAddressDescription,
            getSenderServiceType(el.ServiceType) === ServiceTypeParts.WAREHOUSE,
          );
          el.senderAddress.description = el.SenderAddressDescription;
          el.senderAddress.type = setAddressType(el.SenderAddressDescription);
        }

        if (el.ScheduledDeliveryDate) {
          el.scheduleDeliveryDate = moment(el.ScheduledDeliveryDate).toDate();
        }

        if (el.ArrivalDateTime) {
          el.actualDeliveryDate = moment(
            el.ArrivalDateTime,
            'YYYY-MM-DD HH:mm:ss',
          ).toDate();
        }

        el.deliveryPrice = el.DocumentCost;
        el.StoragePrice = el.StoragePrice ? el.StoragePrice : 0;
        el.SumBeforeCheckWeight =
          parseFloat(el.SumBeforeCheckWeight) > 0
            ? parseFloat(el.SumBeforeCheckWeight)
            : el.deliveryPrice;
        el.announcedPrice = roundToPrecision(el.Cost, 2);
        el.paymentControl =
          (!!el.AfterpaymentOnGoodsCost &&
            parseFloat(el.AfterpaymentOnGoodsCost)) ||
          0;
        el.forwardingCount =
          !!el.ForwardingCount && parseFloat(el.ForwardingCount) > 0
            ? parseFloat(el.ForwardingCount)
            : 0;
        el.ForwardingCount =
          !!el.ForwardingCount && parseFloat(el.ForwardingCount) > 0
            ? parseFloat(el.ForwardingCount)
            : 0;

        el.estimatedDeliveryDate = moment(el.EstimatedDeliveryDate).toDate();

        const date = el.RecipientDateTime || el.ReceivingDateTime;
        el.receivingTime =
          date !== '0001-01-01 00:00:00'
            ? moment(el.RecipientDateTime || el.ReceivingDateTime).toDate()
            : null;

        if (el.PaymentMethod) {
          el.paymentMethod = new InvoicePaymentMethod(el.PaymentMethod);
        }

        el.marketPlaceToken = el.MarketplacePartnerToken;

        el.RedeliveryPaymentCard = el.RedeliveryPaymentCard
          ? el.RedeliveryPaymentCard
          : {
            CardDescription: '',
            CardMaskedNumber: '',
          };

        el.PackingNumber = el.PackingNumber ?? 0;

        el.isInternational = isInternational(el);

        el.state = getState(el.TrackingStatusCode);
        el.statusCode = el.TrackingStatusCode;
        el.allowManageButton = checkAllowManageButton(el);
        el.clientBarcode = el.ClientBarcodes?.[0] || '';
        el.seatsAmount = el.SeatsAmount;
        el.counterpartySenderDescription = el.CounterpartySenderDescription;
        el.CheckWeight = Number(el.CheckWeight) > 0 ? el.CheckWeight : null;
        el.cardMaskedNumber = el.CardMaskedNumber;
        el.isPossibilityDuplicate = el.IsPossibilityDuplicate;
        el.LightReturn = el.LightReturn || null;
        el.possibilityCreateLightReturn = el.possibilityCreateLightReturn || null;
        el.LightReturnNumber = el.LightReturnNumber || null;
        el.addressPickupCostWithDiscount = getAddressPickupCostWithDiscount(el);
        return el;
      }) ?? [],
  };
}

const DOORS_DOORS_SERVICES_FOR_CHECK_WITHOUT_DISCOUNT: string[] = [
  '76a30666-5f44-4b25-a228-ec60b9b6e54e',
  '61117203-2e2a-4bf6-9d76-1fcdb2e96aae',
  '82f754fd-4697-4891-a612-80a8ea49727c',
  '89ab2733-4ede-4215-9e44-801fa9aa5396',
  'e6deec80-6690-4c3e-8f2c-e0286a12384f',
  'f1690b2b-eb96-463d-b9f5-19e3c8cb6856',
];

const DOORS_WAREHOUSE_SERVICES_FOR_CHECK_WITHOUT_DISCOUNT: string[] = [
  '76a30666-5f44-4b25-a228-ec60b9b6e54e',
  '61117203-2e2a-4bf6-9d76-1fcdb2e96aae',
  '82f754fd-4697-4891-a612-80a8ea49727c',
  '89ab2733-4ede-4215-9e44-801fa9aa5396',
  'e6deec80-6690-4c3e-8f2c-e0286a12384f',
  'f1690b2b-eb96-463d-b9f5-19e3c8cb6856',
];

const SERVICE_FOR_ADDRESS_PICKUP_WITH_DISCOUNT: string = 'b2228796-5aa2-4ae9-a1fb-d07364f4d3d8';
const SERVICE_COURIER_PICKUP = SERVICE_FOR_ADDRESS_PICKUP_WITH_DISCOUNT;

export function getAddressPickupCostWithoutDiscount(rawInvoice: any): string {
  if (!Array.isArray(rawInvoice.ServicesCourierCallList)) {
    return '';
  }

  if (rawInvoice.ServicesCourierCallList?.some(s => s.Service === SERVICE_COURIER_PICKUP)) {
    return '';
  }

  if (rawInvoice.ServiceType === ServiceType.DOORS_WAREHOUSE) {
    const service = rawInvoice.ServicesCourierCallList?.find(el => DOORS_WAREHOUSE_SERVICES_FOR_CHECK_WITHOUT_DISCOUNT.includes(el.Service));
    return service?.Cost ? String(parseFloat(service.Cost)) : '';
  }

  if (rawInvoice.ServiceType === ServiceType.DOORS_DOORS) {
    const service = rawInvoice.ServicesCourierCallList?.find(el => DOORS_DOORS_SERVICES_FOR_CHECK_WITHOUT_DISCOUNT.includes(el.Service));
    return service?.Cost ? roundToPrecision(parseFloat(service?.Cost) / 2, 2) : '';
  }

  return '';
}

export function getAddressPickupCostWithDiscount(rawInvoice: any): string {
  if (!Array.isArray(rawInvoice.ServicesCourierCallList)) {
    return '';
  }

  const service = rawInvoice.ServicesCourierCallList?.find(el => el.Service === SERVICE_FOR_ADDRESS_PICKUP_WITH_DISCOUNT);
  return service?.Cost ? String(parseFloat(service.Cost)) : '';
}

export function _getState(invoice: any): InvoiceState {
  const state = new InvoiceState();
  Object.assign(
    state,
    states[invoice.StateId || invoice.StatusCode] || {
      id: '0',
      name: invoice.StateName || invoice.Status,
      shortName: invoice.StateName || invoice.Status
    },
  );
  state.name = invoice.StateName || invoice.Status;

  return state;
}
